import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { subscribeToCountsRealtime } from "../utils/countsService";
import { newIdempotencyKey, downloadCountFrom } from "../utils/downloadsService";
import { isSignedIn, authHeaders } from "../utils/auth";
import { BACKEND_URL } from "../utils/api";
import PrefillBanner from "../components/PrefillBanner";
import { usePrefill } from "../context/PrefillContext";
import { toCommonFormFields } from "../utils/lextriaMapping";
import { saveAs } from "file-saver";

function SimpleFormPage() {
  const { formNumber } = useParams();
  const navigate = useNavigate();
  
  const [formInfo, setFormInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});

  // Lextria Dashboard import: read once and used only as initial values (fields stay editable).
  // Application no/date only go into forms about this patent itself (not 7A, an opposition).
  const { prefill } = usePrefill();
  const [pf] = useState(() => toCommonFormFields(prefill));
  const ownApplicationForm = formNumber === "4" || formNumber === "8";

  // Common fields for all forms
  const [creatorName, setCreatorName] = useState(pf.creatorName || "Chaitra");
  const [trackingNumber, setTrackingNumber] = useState(pf.trackingNumber || "");
  const [title, setTitle] = useState(pf.title || ""); // Title of invention

  // Applicants array (like Form135)
  const [applicants, setApplicants] = useState(() => pf.applicants || [
    { name: "", nationality: "IN", country: "India", address: "" },
  ]);

  // Form 4 specific fields
  const [extensionMonths, setExtensionMonths] = useState("");
  const [signedPersonName, setSignedPersonName] = useState("");
  const [patentNo, setPatentNo] = useState((ownApplicationForm && pf.applicationNo) || "");
  const [reasonForRequest, setReasonForRequest] = useState("");
  const [requestDate, setRequestDate] = useState("");
  const [sectionRule, setSectionRule] = useState("");
  
  // Form 8 specific fields
  const [declarationType, setDeclarationType] = useState("state_claim");
  const [applicationDate, setApplicationDate] = useState((formNumber === "8" && pf.applicationDate) || "");
  const [madeBy, setMadeBy] = useState("");
  const [madeByDeclare, setMadeByDeclare] = useState("");
  const [inventorName, setInventorName] = useState("");
  const [inventorAddress, setInventorAddress] = useState("");
  const [inventorPostalCode, setInventorPostalCode] = useState("");
  const [inventorState, setInventorState] = useState("");
  const [inventorPhone, setInventorPhone] = useState("");
  const [inventorFax, setInventorFax] = useState("");
  const [serviceAddressIndia, setServiceAddressIndia] = useState("");
  const [requestMonth, setRequestMonth] = useState("");
  const [requestYear, setRequestYear] = useState("");
  
  // Form 8 inventors array (for ADD scenario - multiple inventors)
  const [inventors, setInventors] = useState([{ name: "" }]);
  
  // Form 7A specific fields
  const [applicationNo, setApplicationNo] = useState("");
  const [publishedOn, setPublishedOn] = useState("");
  const [grounds, setGrounds] = useState("");
  
  // Form 8A specific fields
  const [inventor, setInventor] = useState("");
  const [patentNumber, setPatentNumber] = useState("");
  const [inventorOr, setInventorOr] = useState("");
  const [patentNumberOr, setPatentNumberOr] = useState("");
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [houseNo, setHouseNo] = useState("");
  const [apartmentStreet, setApartmentStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pincode, setPincode] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  
  // Form 9 specific fields
  const [dateNo, setDateNo] = useState("");
  // patentNo, date, day, year already defined above
  
  // Form 10 specific fields
  const [grantedTo, setGrantedTo] = useState("");
  const [reason, setReason] = useState("");
  // patentNo, date already defined
  
  // Form 11 specific fields
  const [grantTo, setGrantTo] = useState("");
  // reason, patentNo, date already defined
  
  // Lock state to prevent rapid clicks
  const [isGenerating, setIsGenerating] = useState(false);

  // Applicant management functions
  const addApplicant = () =>
    setApplicants((prev) => [
      ...prev,
      { name: "", nationality: "IN", country: "India", address: "" },
    ]);

  const handleApplicantChange = (idx, field, value) => {
    setApplicants((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a))
    );
  };

  const removeApplicant = (idx) =>
    setApplicants((prev) => prev.filter((_, i) => i !== idx));

  // Inventor management functions (for Form 8 ADD scenario)
  const addInventor = () =>
    setInventors((prev) => [...prev, { name: "" }]);

  const handleInventorChange = (idx, value) => {
    setInventors((prev) =>
      prev.map((inv, i) => (i === idx ? { name: value } : inv))
    );
  };

  const removeInventor = (idx) =>
    setInventors((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    // Subscribe to real-time Firebase counts
    const unsubscribe = subscribeToCountsRealtime((data) => {
      setCounts(data);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Fetch form info from backend
    fetch(`${BACKEND_URL}/api/forms`)
      .then((res) => res.json())
      .then((data) => {
        const form = data.forms.find((f) => f.form_number === formNumber);
        if (form) {
          setFormInfo(form);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading form info:", err);
        setLoading(false);
      });
  }, [formNumber]);

  const handleDownload = async () => {
    // Validation - tracking number required for ALL forms
    if (!trackingNumber.trim()) {
      alert("Please enter a tracking number.");
      return;
    }
    
    if (applicants.length === 0 || !applicants[0].name.trim()) {
      alert("Please enter at least one applicant name.");
      return;
    }
    
    // Form 4 specific validations
    if (formNumber === "4") {
      if (!patentNo.trim()) {
        alert("Please enter patent number.");
        return;
      }
      if (!extensionMonths || extensionMonths === "") {
        alert("Please enter extension months.");
        return;
      }
      if (!sectionRule.trim()) {
        alert("Please select section/rule.");
        return;
      }
      if (!reasonForRequest.trim()) {
        alert("Please enter reason for request.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
      if (!requestDate) {
        alert("Please select request date.");
        return;
      }
    }
    
    // Form 8 specific validations
    if (formNumber === "8") {
      if (!patentNo.trim()) {
        alert("Please enter patent/application number.");
        return;
      }
      if (!applicationDate) {
        alert("Please enter application date.");
        return;
      }
      if (declarationType === "state_claim") {
        if (!madeBy.trim()) {
          alert("Please enter who made the original application.");
          return;
        }
        // Validate inventors array
        if (inventors.length === 0 || !inventors[0].name.trim()) {
          alert("Please enter at least one inventor name.");
          return;
        }
      }
      if (declarationType === "declare") {
        if (!madeByDeclare.trim()) {
          alert("Please enter who made the original application.");
          return;
        }
        if (!inventorName.trim()) {
          alert("Please enter inventor name to remove.");
          return;
        }
        // Inventor details only required for REMOVE scenario
        if (!inventorAddress.trim()) {
          alert("Please enter inventor address.");
          return;
        }
      }
      if (!serviceAddressIndia.trim()) {
        alert("Please enter service address in India.");
        return;
      }
      if (!requestDate || !requestMonth.trim() || !requestYear.trim()) {
        alert("Please enter complete request date.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
    }
    
    // Form 7A specific validations
    if (formNumber === "7A") {
      if (!applicationNo.trim()) {
        alert("Please enter application number.");
        return;
      }
      if (!applicationDate) {
        alert("Please enter application date.");
        return;
      }
      if (!madeBy.trim()) {
        alert("Please enter who made the application.");
        return;
      }
      if (!publishedOn) {
        alert("Please enter publication date.");
        return;
      }
      if (!grounds.trim()) {
        alert("Please enter grounds for opposition.");
        return;
      }
      if (!serviceAddressIndia.trim()) {
        alert("Please enter service address in India.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
    }
    
    // Form 8A specific validations
    if (formNumber === "8A") {
      if (!inventor.trim()) {
        alert("Please enter inventor name.");
        return;
      }
      if (!patentNumber.trim()) {
        alert("Please enter patent number.");
        return;
      }
      if (!aadhaarNumber.trim()) {
        alert("Please enter Aadhaar number.");
        return;
      }
      if (!houseNo.trim() || !apartmentStreet.trim() || !city.trim() || !state.trim() || !country.trim() || !pincode.trim()) {
        alert("Please enter complete address details.");
        return;
      }
      if (!mobilePhone.trim()) {
        alert("Please enter mobile phone number.");
        return;
      }
      if (!email.trim()) {
        alert("Please enter email address.");
        return;
      }
      if (!date || !day.trim() || !year.trim()) {
        alert("Please enter complete date.");
        return;
      }
    }
    
    // Form 9 specific validations
    if (formNumber === "9") {
      if (!patentNo.trim()) {
        alert("Please enter patent number.");
        return;
      }
      if (!date || !dateNo.trim() || !day.trim() || !year.trim()) {
        alert("Please enter complete date.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
    }
    
    // Form 10 specific validations
    if (formNumber === "10") {
      if (!patentNo.trim()) {
        alert("Please enter patent number.");
        return;
      }
      if (!date) {
        alert("Please enter date.");
        return;
      }
      if (!grantedTo.trim()) {
        alert("Please enter who the patent was granted to.");
        return;
      }
      if (!serviceAddressIndia.trim()) {
        alert("Please enter service address in India.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
    }
    
    // Form 11 specific validations
    if (formNumber === "11") {
      if (!patentNo.trim()) {
        alert("Please enter patent number.");
        return;
      }
      if (!date) {
        alert("Please enter date.");
        return;
      }
      if (!grantTo.trim()) {
        alert("Please enter who the patent was granted to.");
        return;
      }
      if (!reason.trim()) {
        alert("Please enter reason for the direction request.");
        return;
      }
      if (!serviceAddressIndia.trim()) {
        alert("Please enter service address in India.");
        return;
      }
      if (!signedPersonName.trim()) {
        alert("Please enter name of person who signed.");
        return;
      }
    }
    
    // Other forms validations
    if (formNumber !== "4" && !applicants[0].address.trim()) {
      alert("Please enter applicant address.");
      return;
    }

    // Check auth
    if (!isSignedIn()) {
      navigate("/login");
      return;
    }

    // Prevent rapid clicks
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      // Call backend API
      const response = await fetch(
        `${BACKEND_URL}/api/generate/${formNumber}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": newIdempotencyKey(),
            ...(await authHeaders()),
          },
          body: JSON.stringify({
            creatorName,
            trackingNumber,
            title, // Title of invention
            applicants, // Array of applicants
            // Form 4 specific fields
            ...(formNumber === "4" && {
              patent_no: patentNo,
              extension_months: extensionMonths,
              section_rule: sectionRule,
              reason_for_request: reasonForRequest,
              signed_person_name: signedPersonName,
              request_date: requestDate,
            }),
            
            // Form 8 specific fields
            ...(formNumber === "8" && {
              declaration_type: declarationType,
              patent_no: patentNo,
              application_date: applicationDate,
              made_by: madeBy,
              made_by_declare: madeByDeclare,
              // For ADD scenario - send inventors array
              ...(declarationType === "state_claim" && {
                inventors: inventors.map(inv => ({ name: inv.name })),
              }),
              // For REMOVE scenario - send single inventor details
              ...(declarationType === "declare" && {
                inventor_name: inventorName,
                inventor_address: inventorAddress,
                inventor_postal_code: inventorPostalCode,
                inventor_state: inventorState,
                inventor_phone: inventorPhone,
                inventor_fax: inventorFax,
              }),
              service_address_india: serviceAddressIndia,
              request_date: requestDate,
              request_month: requestMonth,
              request_year: requestYear,
              signed_person_name: signedPersonName,
            }),
            
            // Form 7A specific fields
            ...(formNumber === "7A" && {
              application_no: applicationNo,
              application_date: applicationDate,
              made_by: madeBy,
              published_on: publishedOn,
              grounds: grounds,
              service_address_india: serviceAddressIndia,
              signed_person_name: signedPersonName,
            }),
            
            // Form 8A specific fields
            ...(formNumber === "8A" && {
              inventor: inventor,
              patent_number: patentNumber,
              inventor_or: inventorOr,
              patent_number_or: patentNumberOr,
              aadhaar_number: aadhaarNumber,
              house_no: houseNo,
              apartment_street: apartmentStreet,
              city: city,
              state: state,
              country: country,
              pincode: pincode,
              mobile_phone: mobilePhone,
              email: email,
              date: date,
              day: day,
              year: year,
            }),
            
            // Form 9 specific fields
            ...(formNumber === "9" && {
              patent_no: patentNo,
              date: date,
              date_no: dateNo,
              day: day,
              year: year,
              signature_person_name: signedPersonName,
            }),
            
            // Form 10 specific fields
            ...(formNumber === "10" && {
              patent_no: patentNo,
              date: date,
              granted_to: grantedTo,
              service_address_india: serviceAddressIndia,
              signed_person_name: signedPersonName,
            }),
            
            // Form 11 specific fields
            ...(formNumber === "11" && {
              patent_no: patentNo,
              date: date,
              grant_to: grantTo,
              reason: reason,
              service_address_india: serviceAddressIndia,
              signed_person_name: signedPersonName,
            }),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }

      // Download the file
      const blob = await response.blob();
      saveAs(blob, `FORM${formNumber}-filled.docx`);

      // The backend records the download count and history
      const newCount = downloadCountFrom(response);

      alert(
        newCount != null
          ? `Form ${formNumber} downloaded successfully! Total downloads: ${newCount}`
          : `Form ${formNumber} downloaded successfully!`
      );
    } catch (error) {
      console.error("Error generating form:", error);
      alert("Error generating form: " + error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-xl text-gray-600">Loading form...</div>
        </div>
      </>
    );
  }

  if (!formInfo) {
    return (
      <>
        <Header />
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-xl text-red-600">Form {formNumber} not found!</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-10 px-4">
        <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-lg border border-gray-200">
          <PrefillBanner />
          {/* Form Header */}
          <div className="text-center border-b-2 border-blue-200 pb-6 mb-6">
            <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-bold inline-block mb-3">
              Form {formNumber}
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-2">
              {formInfo.form_title}
            </h1>
            <p className="text-gray-600 text-sm">{formInfo.category}</p>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Creator Name - ALL FORMS (used for Firebase history tracking) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Creator Name <span className="text-red-600">*</span>
              </label>
              <select
                className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
              >
                {pf.creatorName && !["Chaitra", "Mounisha", "Lextria"].includes(pf.creatorName) && (
                  <option value={pf.creatorName}>{pf.creatorName}</option>
                )}
                <option value="Chaitra">Chaitra</option>
                <option value="Mounisha">Mounisha</option>
                <option value="Lextria">Lextria</option>
              </select>
            </div>

            {/* Tracking Number - ALL FORMS (used for Firebase history tracking) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tracking Number <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter tracking number"
                className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
            </div>

            {/* Title of Invention - Only for Forms 2, 6, 7, 7A, 8, 8A, 9, 10 (NOT 4) */}
            {formNumber !== "4" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title of Invention
                </label>
                <input
                  type="text"
                  placeholder="Enter title of invention"
                  className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            )}

            {/* Applicants Section - Always show, but customize label */}
            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex justify-between items-center mb-4">
                <label className="text-sm font-semibold text-gray-700">
                  {formNumber === "4" ? "Applicant(s) (Name Only)" : "Applicant(s)"} <span className="text-red-600">*</span>
                </label>
                <button
                  type="button"
                  onClick={addApplicant}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                >
                  + Add Applicant
                </button>
              </div>

              {applicants.map((applicant, idx) => (
                <div
                  key={idx}
                  className="bg-white p-4 rounded-lg mb-4 border border-gray-200 relative"
                >
                  {applicants.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeApplicant(idx)}
                      className="absolute top-2 right-2 text-red-600 hover:text-red-800 font-bold"
                      title="Remove applicant"
                    >
                      ✕
                    </button>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Applicant Name */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-600 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        placeholder="Applicant name"
                        className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={applicant.name}
                        onChange={(e) =>
                          handleApplicantChange(idx, "name", e.target.value)
                        }
                      />
                    </div>

                    {/* Nationality - Only for Forms 2, 6, 7, 7A, 8, 8A, 9, 10 (NOT 4) */}
                    {formNumber !== "4" && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">
                          Nationality
                        </label>
                        <input
                          type="text"
                          placeholder="IN"
                          className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={applicant.nationality}
                          onChange={(e) =>
                            handleApplicantChange(idx, "nationality", e.target.value)
                          }
                        />
                      </div>
                    )}

                    {/* Country - Only for Forms 2, 6, 7, 7A, 8, 8A, 9, 10 (NOT 4) */}
                    {formNumber !== "4" && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">
                          Country
                        </label>
                        <input
                          type="text"
                          placeholder="India"
                          className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={applicant.country}
                          onChange={(e) =>
                            handleApplicantChange(idx, "country", e.target.value)
                          }
                        />
                      </div>
                    )}

                    {/* Address - Only for Forms 2, 6, 7, 7A, 8, 8A, 9, 10 (NOT 4) */}
                    {formNumber !== "4" && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-600 mb-1">
                          Address
                        </label>
                        <textarea
                          placeholder="Full address"
                          rows="2"
                          className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={applicant.address}
                          onChange={(e) =>
                            handleApplicantChange(idx, "address", e.target.value)
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Form 4 Specific Fields */}
            {formNumber === "4" && (
              <>
                {/* Patent Number */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Patent Number / Application Number
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 202010012345"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={patentNo}
                    onChange={(e) => setPatentNo(e.target.value)}
                  />
                </div>

                {/* Extension Months */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Extension Months (e.g., 3, 6, 9)
                  </label>
                  <input
                    type="number"
                    placeholder="Number of months"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={extensionMonths}
                    onChange={(e) => setExtensionMonths(e.target.value)}
                  />
                </div>

                {/* Section/Rule Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Under Section/Rule <span className="text-red-600">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={sectionRule}
                    onChange={(e) => setSectionRule(e.target.value)}
                  >
                    <option value="">-- Choose Statutory Provision --</option>
                    <option value="Section 53(2) & Rule 80(1A)">Section 53(2) & Rule 80(1A) [Patent Renewal/Maintenance Fee]</option>
                    <option value="Section 142(4) & Rule 131(2)">Section 142(4) & Rule 131(2) [Statement of Working / Form 27]</option>
                    <option value="Rule 12(5)">Rule 12(5) [Foreign Application Details / Form 3]</option>
                    <option value="Rule 13(6)">Rule 13(6) [Declaration as to Inventorship]</option>
                    <option value="Rule 24B(6)">Rule 24B(6) [Standard Request for Examination]</option>
                    <option value="Rule 24C(11)">Rule 24C(11) [Expedited Request for Examination]</option>
                    <option value="Rule 130">Rule 130 [General Form Submissions & Amendments]</option>
                    <option value="Rule 138">Rule 138 [General Condonation of Delay / Timeline Extension]</option>
                  </select>
                </div>

                {/* Reason for Request */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Reason for Request
                  </label>
                  <textarea
                    placeholder="Provide reasons for extension request"
                    rows="3"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={reasonForRequest}
                    onChange={(e) => setReasonForRequest(e.target.value)}
                  />
                </div>

                {/* Name of Person Who Signed */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name of Natural Person Who Signed
                  </label>
                  <input
                    type="text"
                    placeholder="Full name of person signing"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={signedPersonName}
                    onChange={(e) => setSignedPersonName(e.target.value)}
                  />
                </div>

                {/* Request Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Request Date
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={requestDate}
                    onChange={(e) => setRequestDate(e.target.value)}
                  />
                </div>
              </>
            )}

            {/* Form 8 Specific Fields */}
            {formNumber === "8" && (
              <>
                {/* Declaration Type */}
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    What do you want to do? <span className="text-red-600">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={declarationType}
                    onChange={(e) => setDeclarationType(e.target.value)}
                  >
                    <option value="state_claim">ADD someone as inventor (they should be mentioned)</option>
                    <option value="declare">REMOVE someone as inventor (they should NOT be mentioned)</option>
                  </select>
                  
                  {/* Explanation based on selection */}
                  {declarationType === "state_claim" && (
                    <p className="mt-2 text-sm text-blue-700 bg-blue-50 p-3 rounded">
                      📝 You are claiming that <strong>someone should be added</strong> as an inventor in this patent application.
                    </p>
                  )}
                  {declarationType === "declare" && (
                    <p className="mt-2 text-sm text-red-700 bg-red-50 p-3 rounded">
                      🚫 You are declaring that <strong>someone should be removed</strong> as an inventor from this patent application.
                    </p>
                  )}
                </div>
                
                {/* Inventor Name - ALWAYS SHOW but with different label */}
                {declarationType === "state_claim" ? (
                  // ADD scenario - Multiple inventors with + Add button
                  <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <label className="text-sm font-semibold text-gray-700">
                        Name(s) of Inventor(s) to ADD <span className="text-red-600">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={addInventor}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-sm"
                      >
                        + Add Inventor
                      </button>
                    </div>

                    {inventors.map((inventor, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-4 rounded-lg mb-3 border border-gray-200 relative"
                      >
                        {inventors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeInventor(idx)}
                            className="absolute top-2 right-2 text-red-600 hover:text-red-800 font-bold"
                            title="Remove inventor"
                          >
                            ✕
                          </button>
                        )}
                        <label className="block text-sm font-semibold text-gray-600 mb-1">
                          Inventor {idx + 1} Name
                        </label>
                        <input
                          type="text"
                          placeholder="Full name of inventor to be added"
                          className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={inventor.name}
                          onChange={(e) => handleInventorChange(idx, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // REMOVE scenario - Single inventor name
                  <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Name of Inventor to REMOVE <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Full name of person who should NOT be mentioned as inventor"
                      className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={inventorName}
                      onChange={(e) => setInventorName(e.target.value)}
                    />
                  </div>
                )}
                
                {/* Patent Number */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Patent/Application Number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 202010012345"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={patentNo}
                    onChange={(e) => setPatentNo(e.target.value)}
                  />
                </div>
                
                {/* Application Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Application Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={applicationDate}
                    onChange={(e) => setApplicationDate(e.target.value)}
                  />
                </div>
                
                {/* Made By - Who originally filed the patent */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
                  {declarationType === "state_claim" && (
                    <>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Who originally filed this patent application? <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Name of person who filed the original patent application"
                        className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={madeBy}
                        onChange={(e) => setMadeBy(e.target.value)}
                      />
                      <p className="mt-2 text-xs text-gray-600">This is the person who made the original patent application.</p>
                    </>
                  )}
                  
                  {declarationType === "declare" && (
                    <>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Who originally filed this patent application? <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Name of person who filed the original patent application"
                        className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={madeByDeclare}
                        onChange={(e) => setMadeByDeclare(e.target.value)}
                      />
                      <p className="mt-2 text-xs text-gray-600">This is the person who made the original patent application.</p>
                    </>
                  )}
                </div>
                
                {/* Inventor Details - ONLY SHOW FOR REMOVE scenario */}
                {declarationType === "declare" && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-red-900 mb-3">
                      👤 Details of Inventor Being Removed
                    </h3>
                    <p className="text-sm text-red-700 mb-4">
                      Provide complete details of the person who should NOT be mentioned as inventor.
                    </p>
                    
                    {/* Inventor Address */}
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Inventor Address <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        placeholder="Full address of inventor being removed"
                        rows="3"
                        className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={inventorAddress}
                        onChange={(e) => setInventorAddress(e.target.value)}
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Inventor Postal Code */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., 560001"
                          className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={inventorPostalCode}
                          onChange={(e) => setInventorPostalCode(e.target.value)}
                        />
                      </div>
                      
                      {/* Inventor State */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Karnataka"
                          className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={inventorState}
                          onChange={(e) => setInventorState(e.target.value)}
                        />
                      </div>
                      
                      {/* Inventor Phone */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., +91 9876543210"
                          className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={inventorPhone}
                          onChange={(e) => setInventorPhone(e.target.value)}
                        />
                      </div>
                      
                      {/* Inventor Fax */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Fax
                        </label>
                        <input
                          type="text"
                          placeholder="Fax number (optional)"
                          className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          value={inventorFax}
                          onChange={(e) => setInventorFax(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Service Address in India */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Address in India <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    placeholder="Full service address in India"
                    rows="3"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={serviceAddressIndia}
                    onChange={(e) => setServiceAddressIndia(e.target.value)}
                  />
                </div>
                
                {/* Request Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Request Date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={requestDate}
                    onChange={(e) => setRequestDate(e.target.value)}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Request Month - Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Request Month <span className="text-red-600">*</span>
                    </label>
                    <select
                      className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={requestMonth}
                      onChange={(e) => setRequestMonth(e.target.value)}
                    >
                      <option value="">Select Month</option>
                      <option value="January">January</option>
                      <option value="February">February</option>
                      <option value="March">March</option>
                      <option value="April">April</option>
                      <option value="May">May</option>
                      <option value="June">June</option>
                      <option value="July">July</option>
                      <option value="August">August</option>
                      <option value="September">September</option>
                      <option value="October">October</option>
                      <option value="November">November</option>
                      <option value="December">December</option>
                    </select>
                  </div>
                  
                  {/* Request Year */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Request Year <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="number"
                      placeholder="e.g., 2025"
                      className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={requestYear}
                      onChange={(e) => setRequestYear(e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Signed Person Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Signed Person Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Full name of person signing"
                    className="w-full border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={signedPersonName}
                    onChange={(e) => setSignedPersonName(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Download Button */}
          <div className="mt-8 flex flex-col gap-4">
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-all duration-300 relative ${
                isGenerating
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              }`}
            >
              <span className="flex items-center justify-center gap-3">
                {isGenerating ? "Generating..." : `Download Form ${formNumber}`}
                {/* Real-time counter badge */}
                {counts[`FORM${formNumber}.docx`] !== undefined && (
                  <span className="bg-white text-blue-600 px-4 py-1 rounded-full font-bold text-base shadow-md">
                    {counts[`FORM${formNumber}.docx`]}
                  </span>
                )}
              </span>
            </button>
            
            <button
              onClick={() => navigate("/form-selector")}
              className="w-full py-3 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
            >
              ← Back to Form Selector
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default SimpleFormPage;
